import { useTranslation } from 'react-i18next';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import { Box, Button, Card, CardContent, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePromoProtectionPolicy } from '../../hooks';
import { isTreeValid } from '../../lib/conditionTree';
import type { ConditionTreeGroup } from '../../lib/conditionTree';
import { buildPromotionSummary } from '../../lib/promoSummary';
import type { PromoSummaryInput } from '../../lib/promoSummary';

export interface PromotionSummaryRailProps {
  promotion: PromoSummaryInput;
  conditionRoot: ConditionTreeGroup;
  hasPeriod: boolean;
  hasCoupon: boolean;
  promotionId?: string;
  resolveProductSetName?: (productSetId: string) => string | undefined;
}

type CheckState = 'ok' | 'fail' | 'warn';

function CheckIcon({ state }: { state: CheckState }) {
  if (state === 'ok') return <CheckCircleRoundedIcon fontSize="small" color="success" />;
  if (state === 'fail') return <CancelRoundedIcon fontSize="small" color="error" />;
  return <WarningAmberRoundedIcon fontSize="small" color="warning" />;
}

/** Read-only "Summary" rail: a human-readable sentence + a submit checklist (plan-011 §8.3-G). */
export function PromotionSummaryRail({
  promotion,
  conditionRoot,
  hasPeriod,
  hasCoupon,
  promotionId,
  resolveProductSetName,
}: PromotionSummaryRailProps) {
  const { t } = useTranslation('promotions');
  const protectionPolicy = usePromoProtectionPolicy();

  const summary = buildPromotionSummary(t, promotion, { resolveProductSetName });
  const hasAction = promotion.actions.length > 0;
  const conditionsValid = isTreeValid(conditionRoot);

  const maxDiscountPct = promotion.actions.reduce((max, action) => {
    if (action.type === 'PERCENT_OFF' || action.type === 'SHIPPING_PERCENT_OFF') {
      const pct = Number(action.params.percent) || 0;
      return Math.max(max, pct);
    }
    return max;
  }, 0);
  const threshold = protectionPolicy.data?.maxDiscountPctAuto ?? 50;
  const overThreshold = maxDiscountPct > threshold;

  const items: { state: CheckState; label: string }[] = [
    { state: hasAction ? 'ok' : 'fail', label: t('builder.checklist.hasAction') },
    { state: conditionsValid ? 'ok' : 'fail', label: t('builder.checklist.conditionsValid') },
    { state: hasPeriod ? 'ok' : 'warn', label: t('builder.checklist.periodSet') },
  ];
  if (promotion.requiresCoupon) {
    items.push({ state: hasCoupon ? 'ok' : 'fail', label: t('builder.checklist.couponCreated') });
  }
  if (overThreshold) {
    items.push({
      state: 'warn',
      label: t('builder.checklist.overThreshold', { percent: maxDiscountPct, threshold }),
    });
  }

  return (
    <Card variant="outlined" sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {t('builder.summaryTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {summary}
        </Typography>

        <List dense disablePadding sx={{ mb: 2 }}>
          {items.map((item, index) => (
            // Static, order-stable checklist — index is a safe key here.
            <ListItem key={index} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckIcon state={item.state} />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ variant: 'body2' }}
                primary={item.label}
              />
            </ListItem>
          ))}
        </List>

        <Box>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<PlayCircleOutlineRoundedIcon />}
            component={RouterLink}
            to={promotionId ? `/promo/simulator?promotionId=${promotionId}` : '/promo/simulator'}
          >
            {t('builder.testInSimulator')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
